"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IkeDraw = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const mobx_1 = require("mobx");
const mobx_utils_1 = require("mobx-utils");
const id_1 = require("../id");
const model_1 = require("../model");
const draw_state_1 = require("./draw.state");
const draw_util_1 = require("./draw.util");
const svg_event_1 = require("./draw/svg/svg.event");
const svg_wheel_1 = require("./draw/svg/svg.wheel");
class IkeDraw {
    get drawState() {
        // DrawableStates are frequently discarded (such as when discarding edits),
        // so fetch them on demand to avoid leaks.
        return this.collection.getDrawableState(this.capture.objectId);
    }
    constructor(ctx) {
        var _a;
        this.id = 'ds_' + id_1.IkeId.generate();
        this.isMarkable = false;
        /** List of images already loaded by the browser */
        this.imageMap = mobx_1.observable.map(null, { deep: false, name: `Draw-ImageLoaded-${this.id}` });
        this._lastUnits = null;
        this._getLabelText = null;
        this._getBrightness = null;
        this._getCrop = null;
        this.log = ctx.log;
        this.collection = ctx.collection;
        this.capture = ctx.capture;
        this.brightnessCorrectionState = this.collection.getBrightnessCorrectionState(ctx.capture.objectId);
        this.imageVisibilityState = this.collection.getImageVisibilityState(ctx.capture.objectId);
        this.cropParameterState = this.collection.getCropParameterState(ctx.capture.objectId);
        this.state = new draw_state_1.IkeDrawState(this);
        this.window = (_a = ctx.window) !== null && _a !== void 0 ? _a : null;
        if (ctx.captureMaskFetcher) {
            this.captureMaskFetcher = ctx.captureMaskFetcher;
        }
        this.orientation = this.capture.resolution.width > this.capture.resolution.height ? 'landscape' : 'portrait';
        this.init();
        this.boundWheelEvent = el => svg_wheel_1.SVGWheel.onWheel(this, el);
        this.boundEvents = new svg_event_1.SVGEventHandler(this, ctx.onStartHOASession);
    }
    /** Use a transformer to cache the drawableId to text conversions they dont change very often. */
    getLabelText(drawableId, options) {
        var _a, _b;
        if (options == null || options.units == null) {
            return [];
        }
        const units = options.units;
        if (this._lastUnits !== units) {
            this._lastUnits = units;
            this._getLabelText = (drawId, ctx) => {
                const drawable = this.drawState.getDrawable(drawId);
                if (model_1.CollectionModelDrawableMarker.isMarker(drawable)) {
                    return draw_util_1.IkeDrawUtil.getMarkerLabelText(this.capture, drawable, ctx);
                }
                if (model_1.CollectionModelDrawableMarkerOffset.isBaseOffset(drawable)) {
                    return draw_util_1.IkeDrawUtil.getBaseOffsetLabelText(this.capture, drawable);
                }
                if (model_1.CollectionModelDrawableLine.isLine(drawable)) {
                    return draw_util_1.IkeDrawUtil.getLineLabelText(drawable, ctx);
                }
                if (drawable instanceof model_1.CollectionModelDrawableVerticalSeparation) {
                    return drawable.label.text;
                }
                return [];
            };
        }
        return (_b = (_a = this._getLabelText) === null || _a === void 0 ? void 0 : _a.call(this, drawableId, options)) !== null && _b !== void 0 ? _b : [];
    }
    /**
     * Returns an array of the first 4 colors of spans if the Marker belongs to Span
     * @param drawableId formID from marker
     */
    getSpanColors(drawableId) {
        const drawable = this.drawState.getDrawable(drawableId);
        const label = drawable.label;
        const labelLinks = label.links;
        let colors = [];
        for (const link of labelLinks) {
            const color = model_1.FormModelFieldSubForm.getSpanColor(link.parent);
            if (color && colors.indexOf(color) === -1) {
                colors.push(color);
            }
        }
        return colors ? colors.slice(0, 4) : null;
    }
    getBrightness(brightnessCorrectionId) {
        this._getBrightness = (0, mobx_utils_1.createTransformer)((brightnessId) => {
            const brightnessCorrection = this.brightnessCorrectionState.getBrightnessCorrection(brightnessId);
            if (model_1.CollectionModelGammaBrightnessCorrection.isGamma(brightnessCorrection)) {
                return brightnessCorrection.exponent;
            }
            return 1;
        });
        return this._getBrightness(brightnessCorrectionId);
    }
    getCrop(cropParameterId) {
        this._getCrop[0] = (0, mobx_utils_1.createTransformer)((cropId) => {
            const cropParameter = this.cropParameterState.getCropParameter(cropId);
            if (model_1.CollectionModelFreeCropParameter.isFree(cropParameter)) {
                return cropParameter;
            }
            return 1;
        });
        return this._getCrop(cropParameterId);
    }
    setImageLoaded(imageId, loaded = true) {
        if (this.imageMap.has(imageId)) {
            return;
        }
        this.imageMap.set(imageId, loaded);
    }
    /** Pre load the image into the browser cache */
    loadImage(imageId, url) {
        if (this.imageMap.has(imageId)) {
            return;
        }
        const image = new Image();
        image.onload = () => this.setImageLoaded(imageId);
        image.onerror = () => this.setImageLoaded(imageId, false);
        image.src = url;
    }
    isImageReady(imageId) {
        return this.imageMap.get(imageId) === true;
    }
    init() {
        if (this.capture != null) {
            if (this.state.cropHeight > 0 && this.state.cropWidth > 0 && !this.isMarkable) {
                this.state.setCenter(this.state.cropX + (this.state.cropWidth * 0.5), this.state.cropY + (this.state.cropHeight * 0.5));
            }
            else {
                this.state.setCenter(this.capture.resolution.width / 2, this.capture.resolution.height / 2);
            }
        }
        this.state.selectedId = null;
        this.state.hoverId = null;
        this.state.setTool(null);
        this.state.setCrosshair(false);
        if (this.window) {
            this.state.isDateLabelVisible = false;
            this.state.isBearingLabelVisible = false;
        }
        else {
            this.state.isDateLabelVisible = true;
            this.state.isBearingLabelVisible = true;
        }
        if (!this.isIkePhoto) {
            this.state.setCrosshair(true);
        }
        else if (!this.drawState.hasUserMeasurements()) {
            this.state.setCrosshair(true);
        }
    }
    get geometry3d() {
        return this.collection.state.getGeometry(this.capture.objectId);
    }
    /** Does this draw state have enough information for photo measure */
    get isIkePhoto() {
        return this.capture.type === core_api_1.IkeCaptureType.TRUE_SIZE;
    }
    /** are any elements on the page currently moving */
    get isMoving() {
        return this.state.moveId != null;
    }
    get isLinking() {
        return this.state.linkFieldIds.length > 0;
    }
    get selected() {
        if (this.state.selectedId == null) {
            return null;
        }
        if (this.state.selectedId === 'image') {
            return null;
        }
        if (!this.drawState.hasDrawable(this.state.selectedId)) {
            return null;
        }
        return this.drawState.getDrawable(this.state.selectedId);
    }
    get highlighted() {
        if (this.state.hoverId == null) {
            return null;
        }
        if (this.state.hoverId === 'image') {
            return null;
        }
        if (!this.drawState.hasDrawable(this.state.hoverId)) {
            return null;
        }
        return this.drawState.getDrawable(this.state.hoverId);
    }
}
exports.IkeDraw = IkeDraw;
tslib_1.__decorate([
    mobx_1.observable
], IkeDraw.prototype, "isMarkable", void 0);
tslib_1.__decorate([
    mobx_1.action
], IkeDraw.prototype, "setImageLoaded", null);
tslib_1.__decorate([
    mobx_1.action
], IkeDraw.prototype, "init", null);
tslib_1.__decorate([
    mobx_1.computed
], IkeDraw.prototype, "isIkePhoto", null);
tslib_1.__decorate([
    mobx_1.computed
], IkeDraw.prototype, "isMoving", null);
tslib_1.__decorate([
    mobx_1.computed
], IkeDraw.prototype, "isLinking", null);
tslib_1.__decorate([
    mobx_1.computed
], IkeDraw.prototype, "selected", null);
tslib_1.__decorate([
    mobx_1.computed
], IkeDraw.prototype, "highlighted", null);
//# sourceMappingURL=draw.js.map